import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { FileUp, Percent, Plus } from 'lucide-react';
import { useCashflow } from './hooks/useCashflow';
import {
  AccrualDialog,
  AccrualsTable,
  CashflowCards,
  CashflowCharts,
  CommissionsDialog,
  SettlementDialog,
  SettlementsTable,
  StatementImportDialog,
} from './components';
import { CashflowPageProps, CashflowTab, PeriodPreset } from './types';

const PERIOD_LABELS: Record<PeriodPreset, string> = {
  all: 'Весь период',
  '7d': '7 дней',
  '30d': '30 дней',
  month: 'Этот месяц',
  prevMonth: 'Прошлый месяц',
};

/** Вкладки таблицы: деньги на счету и продажи, из которых они складываются. */
const TABS: Array<{ key: CashflowTab; title: string; hint: string }> = [
  {
    key: 'settlements',
    title: 'Поступления',
    hint: 'Зачисления на счёт — из выписки банка (🏦) или вручную по отчёту провайдера',
  },
  {
    key: 'accruals',
    title: 'Начисления',
    hint: 'Продажи у провайдеров без платежей в базе (Apple) — по одной записи на покупку',
  },
  {
    key: 'charts',
    title: 'Графики',
    hint: 'Сколько собрано по провайдерам и как это распределилось по дням',
  },
];

export default function CashflowPage({ isDark }: CashflowPageProps) {
  const {
    overview,
    providers,
    isLoading,
    period,
    setPeriod,
    isSettlementDialogOpen,
    setIsSettlementDialogOpen,
    isCommissionsDialogOpen,
    setIsCommissionsDialogOpen,
    editingSettlement,
    formData,
    setFormData,
    isSubmitting,
    openCreate,
    openEdit,
    handleSubmit,
    handleDelete,
    tab,
    setTab,
    isAccrualDialogOpen,
    setIsAccrualDialogOpen,
    editingAccrual,
    accrualForm,
    setAccrualForm,
    openAccrualCreate,
    openAccrualEdit,
    handleAccrualSubmit,
    handleAccrualDelete,
    handleUpdateProvider,
    refresh,
  } = useCashflow();
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Валюта у каждой записи своя: Apple считается в долларах, остальные в сумах.
  const formatMoney = (amount: number, currency = 'UZS') =>
    new Intl.NumberFormat(currency === 'UZS' ? 'uz-UZ' : 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  /** Начисления заводятся только у ручных провайдеров (см. ADR 0021). */
  const manualProviders = providers.filter((p) => p.is_active && p.source === 'manual');

  const emptyTotal = { gross: 0, net: 0, settled: 0, outstanding: 0 };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>
            Cashflow
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Сверка: собрано провайдерами → чистое после комиссии → поступило на счёт →
            осталось получить
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={(value) => setPeriod(value as PeriodPreset)}>
            <SelectTrigger
              className={cn(
                'w-[170px] h-10 rounded-xl border-0',
                isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900',
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={cn(isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
              {(Object.keys(PERIOD_LABELS) as PeriodPreset[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {PERIOD_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            onClick={() => setIsCommissionsDialogOpen(true)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            <Percent className="w-4 h-4 mr-2" />
            Комиссии
          </Button>

          <Button
            variant="outline"
            onClick={() => setIsImportOpen(true)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            <FileUp className="w-4 h-4 mr-2" />
            Импорт выписки
          </Button>

          <Button
            onClick={tab === 'accruals' ? openAccrualCreate : openCreate}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            {tab === 'accruals' ? 'Добавить начисление' : 'Добавить поступление'}
          </Button>
        </div>
      </div>

      <CashflowCards
        isDark={isDark}
        providers={overview?.providers || []}
        total={overview?.total || emptyTotal}
        isLoading={isLoading}
        formatMoney={formatMoney}
      />

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden transition-all duration-300',
          isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <div className={cn('p-5 lg:p-6 border-b', isDark ? 'border-slate-700' : 'border-slate-200')}>
          <div className="flex flex-wrap items-center gap-2">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={cn(
                  'px-4 py-2 rounded-xl text-sm font-medium transition-colors',
                  tab === item.key
                    ? 'bg-[#C9A227] text-[#111111]'
                    : isDark
                      ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                )}
              >
                {item.title}
                {item.key === 'settlements' && ` (${overview?.settlements.length ?? 0})`}
                {item.key === 'accruals' && ` (${overview?.accruals.length ?? 0})`}
              </button>
            ))}
          </div>
          <p className={cn('text-sm mt-3', isDark ? 'text-slate-400' : 'text-slate-500')}>
            {TABS.find((item) => item.key === tab)?.hint}
          </p>
        </div>
        {tab === 'settlements' && (
          <SettlementsTable
            isDark={isDark}
            settlements={overview?.settlements || []}
            isLoading={isLoading}
            onEdit={openEdit}
            onDelete={handleDelete}
            formatMoney={formatMoney}
          />
        )}
        {tab === 'accruals' && (
          <AccrualsTable
            isDark={isDark}
            accruals={overview?.accruals || []}
            isLoading={isLoading}
            onEdit={openAccrualEdit}
            onDelete={handleAccrualDelete}
            formatMoney={formatMoney}
          />
        )}
        {tab === 'charts' && (
          <div className="p-4 lg:p-5">
            <CashflowCharts
              isDark={isDark}
              providers={overview?.providers || []}
              daily={overview?.daily || []}
              formatMoney={formatMoney}
            />
          </div>
        )}
      </Card>

      <AccrualDialog
        isDark={isDark}
        isOpen={isAccrualDialogOpen}
        onOpenChange={setIsAccrualDialogOpen}
        providers={manualProviders}
        formData={accrualForm}
        onFormDataChange={setAccrualForm}
        onSubmit={handleAccrualSubmit}
        isEditing={Boolean(editingAccrual)}
        isSubmitting={isSubmitting}
        formatMoney={formatMoney}
      />

      <SettlementDialog
        isDark={isDark}
        isOpen={isSettlementDialogOpen}
        onOpenChange={setIsSettlementDialogOpen}
        providers={providers}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleSubmit}
        isEditing={Boolean(editingSettlement)}
        isSubmitting={isSubmitting}
        isBankConfirmed={Boolean(editingSettlement?.bank_fingerprint)}
      />

      <StatementImportDialog
        isDark={isDark}
        isOpen={isImportOpen}
        onOpenChange={setIsImportOpen}
        onImported={refresh}
        formatMoney={formatMoney}
      />

      <CommissionsDialog
        isDark={isDark}
        isOpen={isCommissionsDialogOpen}
        onOpenChange={setIsCommissionsDialogOpen}
        providers={providers}
        onUpdate={handleUpdateProvider}
      />
    </div>
  );
}
